import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import AdminTableHeader from '../../../components/Admin/AdminTableHeader';
import { NoRecordFound } from '../../../components/Common/NoRecordFound';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import { TokenCookieManager } from '../../../utils/tokenCookies';
import StoryConfigForm from './StoryConfigForm';
import {
  AgeConfigModel,
  StoryConfigAdminModel,
  AGE_GROUP_LABELS,
  AGE_GROUP_ORDER,
  getAdminStoryConfig,
  updateAdminStoryConfig,
} from './services/StoryConfig.service';

interface AgeRow {
  ageKey: string;
  label: string;
  config: AgeConfigModel;
}

const StoryConfig = () => {
  const [config, setConfig] = useState<StoryConfigAdminModel | null>(null);
  const [rows, setRows] = useState<AgeRow[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingAgeKey, setEditingAgeKey] = useState<string | null>(null);
  const [editingConfig, setEditingConfig] = useState<AgeConfigModel | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!TokenCookieManager.hasToken()) {
      navigate('/admin');
    }
  }, [navigate]);

  useEffect(() => {
    loadConfig();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadConfig = async () => {
    setLoading(true);
    const result = await getAdminStoryConfig();
    setConfig(result);
    if (result) {
      const ageRows: AgeRow[] = AGE_GROUP_ORDER
        .filter((key) => result[key])
        .map((key) => ({
          ageKey: key,
          label: AGE_GROUP_LABELS[key] || key,
          config: result[key] as AgeConfigModel,
        }));

      // Add any extra keys not in our predefined order
      Object.keys(result).forEach((key) => {
        if (key === 'id' || AGE_GROUP_ORDER.includes(key)) return;
        if (typeof result[key] === 'object' && result[key] !== null) {
          ageRows.push({
            ageKey: key,
            label: AGE_GROUP_LABELS[key] || key,
            config: result[key] as AgeConfigModel,
          });
        }
      });

      setRows(ageRows);
    }
    setLoading(false);
  };

  const handleEdit = (row: AgeRow) => {
    setEditingAgeKey(row.ageKey);
    setEditingConfig(row.config);
    setShowForm(true);
  };

  const handleSave = async (ageKey: string, data: AgeConfigModel) => {
    if (!config?.id) return;
    const success = await updateAdminStoryConfig(config.id, ageKey, data, () => {
      setShowForm(false);
      loadConfig();
    });
    if (!success) {
      // Keep form open on failure
    }
  };

  return (
    <div className="p-0 md:p-4">
      {showForm && editingAgeKey && editingConfig ? (
        <StoryConfigForm
          setShowForm={setShowForm}
          ageKey={editingAgeKey}
          ageConfig={editingConfig}
          onSave={handleSave}
        />
      ) : (
        <>
          {loading ? (
            <LoadingSpinner />
          ) : (
            <div className="w-full overflow-x-auto">
              <table className="min-w-full border border-gray-300">
                <AdminTableHeader
                  columns={[
                    { key: 'sn', label: 'S#' },
                    { key: 'level', label: 'Reading Level' },
                    { key: 'words', label: 'Words Per Page' },
                    { key: 'maxText', label: 'Max Text Length' },
                    { key: 'rows', label: 'Textarea Rows' },
                    { key: 'actions', label: '' },
                  ]}
                />
                <tbody>
                  {rows.length > 0 ? (
                    rows.map((row, index) => (
                      <tr key={row.ageKey} className={index % 2 === 0 ? 'bg-gray-100' : ''}>
                        <td className="text-left py-2 px-4 border-b">{index + 1}</td>
                        <td className="text-left py-2 px-4 border-b font-medium">{row.label}</td>
                        <td className="text-left py-2 px-4 border-b">{row.config.words_total}</td>
                        <td className="text-left py-2 px-4 border-b">{row.config.max_text_length || '-'}</td>
                        <td className="text-left py-2 px-4 border-b">{row.config.rows_total || '-'}</td>
                        <td className="text-left py-4 px-4 border-b">
                          <FontAwesomeIcon
                            icon={faPenToSquare}
                            className="cursor-pointer text-storyblue hover:text-blue-700"
                            title="Edit Reading Level"
                            onClick={() => handleEdit(row)}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <NoRecordFound colSpan={6} />
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StoryConfig;
